import { forwardRef } from 'react';

import dayjs from 'dayjs';

import { ChartCardWithRef } from '../../../../components/ChartCard';
import type { Insight } from '../../../../domain/insight';
import { InsightType } from '../../../../domain/insightType';
import { InsightSignalType } from '../../../../domain/signal';
import { SAM_LANGUAGE_CODE } from '../../../../domain/user';
import { useUserSettings } from '../../../../features/user/useUserSettings';
import { WORLDWIDE_COUNTRY_ID } from '../../../../services/country/country';
import InsightsTextService from '../../../../services/insightsTextService';
import { formatToMonthYear } from '../../../../services/timeFormat';

import getGraphPeriod from './getGraphPeriod';
import { OpportunityGraph } from './OpportunityGraph';
import { TrafficChangeGraph } from './TrafficChangeGraph';

export const InsightGraph = forwardRef<HTMLDivElement, { insight: Insight }>(({ insight }, ref) => {
  const { getUserSetting } = useUserSettings();
  const languageCode = getUserSetting(SAM_LANGUAGE_CODE);

  const isTrafficSignal = insight.insightType === InsightType.Signal && insight.type === InsightSignalType.Traffic;
  const isOpportunity = insight.insightType === InsightType.Opportunity;
  const isSiteTrends = insight.insightType === InsightType.SiteTrends;

  const countryCode = () => {
    if (isOpportunity || isSiteTrends) {
      return insight.country;
    }
    if (isTrafficSignal) {
      return insight.siteTrends[0].country;
    }

    return WORLDWIDE_COUNTRY_ID;
  };

  const graphDateText = () => {
    if (isOpportunity) {
      return formatToMonthYear(insight.dataDate);
    }
    if (isSiteTrends) {
      const [startDate, endDate] = getGraphPeriod(insight);
      return <span>{`${startDate} - ${endDate}`}</span>;
    }
    if (isTrafficSignal) {
      const [startDate, endDate] = getGraphPeriod(insight.siteTrends[0]);
      return <span>{`${startDate} - ${endDate}`}</span>;
    }

    return formatToMonthYear(dayjs());
  };

  const isWithExtendedHeader = isOpportunity || isTrafficSignal || isSiteTrends;

  return (
    <ChartCardWithRef
      ref={ref}
      title={InsightsTextService.getInsightTranslatedText(insight, languageCode)}
      date={graphDateText()}
      compact={isWithExtendedHeader}
      withBorder={isOpportunity}
      countryCode={countryCode()}
      extendedFooterPadding={!isOpportunity}
      dataAutomation="EmailBenchmark"
    >
      {isOpportunity ? <OpportunityGraph insight={insight} /> : null}
      {isTrafficSignal || insight.insightType === InsightType.SiteTrends ? (
        <TrafficChangeGraph insight={insight} />
      ) : null}
    </ChartCardWithRef>
  );
});
